import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, Globe, CheckCircle2, Lock, Banknote } from "lucide-react";

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-12 lg:pt-20 pb-20 lg:pb-32" data-testid="hero-section">
      <div className="absolute inset-0 grid-pattern opacity-50 pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-[36rem] h-[36rem] rounded-full bg-gradient-to-br from-[#00C853]/20 to-transparent blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[36rem] h-[36rem] rounded-full bg-gradient-to-br from-[#0B3C5D]/15 to-transparent blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-[#0B3C5D]"
            data-testid="hero-pill"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-[#00C853]" />
            SSL secured · transparent rates · Reviewed Payouts
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#0B3C5D] leading-[1.05] tracking-tight"
          >
            Sell gift cards{" "}
            <span className="gradient-text">safely</span>
            <br className="hidden sm:block" /> & get paid in <em className="not-italic text-[#00C853]">hours</em>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed"
          >
            Trusted platform with real-time rates and reviewed payouts. Most cards are paid within
            60 minutes to 24 hours via PayPal, Crypto, Bank Transfer & more.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <Link
              to="/sell"
              className="inline-flex items-center justify-center rounded-full bg-[#0B3C5D] text-white px-7 py-3.5 text-base font-semibold btn-primary"
              data-testid="hero-sell-now"
            >
              Sell Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/rates"
              className="inline-flex items-center justify-center rounded-full bg-white border border-slate-200 text-[#0B3C5D] px-7 py-3.5 text-base font-semibold hover:border-[#0B3C5D] transition-colors"
              data-testid="hero-check-rates"
            >
              Check Rates
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-10 grid grid-cols-3 gap-4 max-w-xl"
          >
            {[
              { Icon: ShieldCheck, label: "SSL Encrypted" },
              { Icon: Zap, label: "Paid in 1–24h" },
              { Icon: Globe, label: "US · UK · UAE · CA" },
            ].map(({ Icon, label }, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#00C853]" />
                </div>
                <span className="font-medium">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right dashboard mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#0B3C5D]/10 to-[#00C853]/10 rounded-3xl blur-2xl" />
            <div className="relative bg-white border border-slate-200 rounded-3xl shadow-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Live Payout</p>
                  <p className="font-display text-3xl font-bold text-[#0B3C5D] mt-1">$486.00</p>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#00C853] bg-[#00C853]/10 px-2.5 py-1 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Paid
                  </span>
                  <p className="text-[11px] text-slate-500 mt-1">GPY-A4F2</p>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between text-sm bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0B3C5D] text-white flex items-center justify-center font-display font-bold text-xs">A</div>
                  <div>
                    <p className="font-medium text-slate-900">Amazon Gift Card</p>
                    <p className="text-xs text-slate-500">$500 · US</p>
                  </div>
                </div>
                <p className="font-display font-bold text-[#0B3C5D]">92%</p>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[
                  { Icon: ShieldCheck, label: "Verified" },
                  { Icon: Lock, label: "Encrypted" },
                  { Icon: Banknote, label: "Paid" },
                ].map(({ Icon, label }, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-2.5 text-center">
                    <Icon className="w-4 h-4 mx-auto text-[#00C853]" />
                    <p className="text-[11px] mt-1 text-slate-600 font-medium">{label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-slate-200">
                <p className="text-xs text-slate-500">Payout method</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-900">PayPal · m***@gmail.com</span>
                  <span className="text-xs text-slate-500">2 min ago</span>
                </div>
              </div>
            </div>

            {/* Floating mini card */}
            <motion.div
              initial={{ opacity: 0, x: -20, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="absolute -left-6 -bottom-6 bg-white border border-slate-200 rounded-2xl shadow-xl p-4 w-56 hidden sm:block"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-slate-500">Today</span>
                <span className="text-[11px] text-[#00C853] font-medium">+12%</span>
              </div>
              <p className="font-display text-xl font-bold text-[#0B3C5D] mt-1">$12,840</p>
              <p className="text-[11px] text-slate-500">paid out in last 24h</p>
              <div className="mt-3 h-8 flex items-end gap-1">
                {[20, 35, 28, 50, 42, 65, 78].map((h, i) => (
                  <div key={i} className="flex-1 bg-gradient-to-t from-[#00C853]/60 to-[#00C853] rounded-sm" style={{ height: `${h}%` }} />
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
